import React from "react";

import { useMediaQuery } from "../../hooks/useMediaQuery";
import TeamCrest from "./shared/TeamCrest";
import FormChips from "./shared/FormChips";
import ZoneLegend, { promotionsFromRows } from "./shared/ZoneLegend";
import { useCompetitionsTr } from "./shared/useCompetitionsTr";
import { formatDiff, formatGoals } from "./shared/competitionsFormat";
import { canonicalPromotion } from "./shared/competitionsZones";

// CompetitionStandingsTable — embeddable, self-contained standings table for
// the Competitions build (UI-SPEC §7 / K2b §14 / K9 §20).
//
// PROPS IN, MARKUP OUT: no API calls, no router, no page context. Navigation
// is injected via `onNavigate(row)`; a custom per-row action slot via
// `renderRowAction(row)` (render prop) replaces the default row navigation
// affordance. See TP-CompetitionPage/build/CONTRACTS.md for the shapes.
//
// Mobile (declared BEFORE build, CLAUDE.md UI rule 1): at ≤767px the table
// switches to the K9 card pattern (one card per team, zone key keeps the left
// edge, subline "Sp 24 · 62:20 · Diff +42", mini form chips 14×14). Nothing is
// shrunk or cut off; the legend wraps.
//
// "Empty renders nothing": no rows -> null. No '-'/'n/a' placeholders anywhere.

const BORDER = "#E2E8F0";
const ROW_DIVIDER = "#F4F7FA";
const HEAD_BG = "#F8FAFC";

// One-time scoped hover CSS (self-contained — no global stylesheet edit).
const SCOPED_CSS = `
.tp-comp-row--click { cursor: pointer; }
.tp-comp-row--click:hover, .tp-comp-card--click:hover { background: #F8FBFF; }
`;

// Full table (K1 @1440): (zone) # Team Sp S U N Tore Diff Pkt Form.
const FULL_COLS = "8px 40px minmax(0,1fr) 54px 44px 44px 44px 74px 60px 60px 132px";
// Compact table (K2b group card): (zone) # Team Sp Diff Tore Pkt.
const COMPACT_COLS = "8px 34px minmax(0,1fr) 40px 40px 68px 52px";
const ACTION_COL = " max-content";

const numStyle = { fontVariantNumeric: "tabular-nums" };

// Zone bar: canonical zone mapping (competitionsZones.js) — spec palette for
// the five spec zones, provider passthrough for anything else (QA2 defect 3).
const ZoneKey = ({ promotion, height }) => {
  const zone = canonicalPromotion(promotion);
  return (
    <span
      data-testid="zone-key"
      style={{
        width: 3,
        height: height || "100%",
        alignSelf: height ? undefined : "stretch",
        background: (zone && zone.color) || "transparent",
        flex: "none",
        justifySelf: "start",
      }}
      title={(zone && zone.name) || undefined}
    />
  );
};

const headCell = (label, { first, align = "right", color = "#64748B" } = {}) => (
  <span
    key={label || "zone"}
    style={{
      fontSize: 11.5,
      fontWeight: 600,
      color,
      textAlign: align,
      ...numStyle,
    }}
  >
    {label}
  </span>
);

const CompetitionStandingsTable = ({
  rows,
  promotions,
  onNavigate,
  navigable, // optional (row) => bool: per-row affordance gate (no dead cursor
  // on rows the page cannot resolve to a profile — TOTW pattern)
  renderRowAction,
  variant = "full", // "full" (K1 11-column) | "compact" (K2b group card)
  showLegend = true,
  squareTop = false, // group cards: table joins a title bar above (K2b)
}) => {
  const { tr } = useCompetitionsTr();
  const isMobile = useMediaQuery("(max-width: 767px)");

  if (!Array.isArray(rows) || rows.length === 0) return null; // empty renders nothing

  const legendItems =
    promotions && promotions.length ? promotions : promotionsFromRows(rows);
  const hasAction = typeof renderRowAction === "function";
  const clickable = !hasAction && typeof onNavigate === "function";
  const rowClickable = (row) =>
    clickable && (typeof navigable !== "function" || Boolean(navigable(row)));
  const rowClick = (row) => (rowClickable(row) ? () => onNavigate(row) : undefined);

  // ── Mobile: K9 card pattern ────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="tp-comp-standings" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <style>{SCOPED_CSS}</style>
        {rows.map((row) => {
          const subParts = [];
          if (row.played != null) subParts.push(`${tr("Sp")} ${row.played}`);
          const goals = formatGoals(row.goals_for, row.goals_against);
          if (goals) subParts.push(goals);
          const diff = formatDiff(row.goal_diff);
          if (diff) subParts.push(`${tr("Diff")} ${diff}`);
          return (
            <div
              key={(row.team && row.team.id) || `pos-${row.position}`}
              data-testid="standings-card"
              className={rowClickable(row) ? "tp-comp-card--click" : undefined}
              onClick={rowClick(row)}
              style={{
                display: "flex",
                alignItems: "stretch",
                border: `1px solid ${BORDER}`,
                borderRadius: 3,
                minHeight: 64,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <ZoneKey promotion={row.promotion} />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 14px",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span style={{ fontSize: 14, color: "#64748B", width: 16, textAlign: "right", ...numStyle }}>
                  {row.position}
                </span>
                <TeamCrest team={row.team} size={28} />
                <div style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  <span
                    style={{
                      fontSize: 14.5,
                      fontWeight: 600,
                      color: "#0F172A",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {row.team && row.team.name}
                  </span>
                  {subParts.length > 0 && (
                    <span style={{ fontSize: 12, color: "#64748B", ...numStyle }}>
                      {subParts.join(" · ")}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", ...numStyle }}>
                    {row.points}
                  </span>
                  <FormChips form={row.form} size={14} fontSize={8} gap={3} />
                </div>
                {hasAction && (
                  <span data-testid="row-action-slot" style={{ flex: "none" }}>
                    {renderRowAction(row)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {showLegend && <ZoneLegend promotions={legendItems} swatchHeight={13} fontSize={12} />}
      </div>
    );
  }

  // ── Desktop: grid table ────────────────────────────────────────────────────
  const compact = variant === "compact";
  const cols = (compact ? COMPACT_COLS : FULL_COLS) + (hasAction ? ACTION_COL : "");
  const rowMinHeight = compact ? 44 : 48;
  const gridStyle = {
    display: "grid",
    gridTemplateColumns: cols,
    alignItems: "center",
    gap: 10,
    padding: compact ? "0 18px 0 0" : "0 20px 0 0",
  };

  const headers = compact
    ? [
        headCell("", { align: "left" }),
        headCell("#"),
        headCell(tr("Team"), { align: "left" }),
        headCell(tr("Sp")),
        headCell(tr("Diff")),
        headCell(tr("Tore")),
        headCell(tr("Pkt"), { color: "#0F172A" }),
      ]
    : [
        headCell("", { align: "left" }),
        headCell("#"),
        headCell(tr("Team"), { align: "left" }),
        headCell(tr("Sp")),
        headCell(tr("S")),
        headCell(tr("U")),
        headCell(tr("N")),
        headCell(tr("Tore")),
        headCell(tr("Diff")),
        headCell(tr("Pkt"), { color: "#0F172A" }),
        headCell(tr("Form")),
      ];

  return (
    <div className="tp-comp-standings" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <style>{SCOPED_CSS}</style>
      <div
        style={{
          border: `1px solid ${BORDER}`,
          borderRadius: squareTop ? "0 0 3px 3px" : 3,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <div
          style={{
            ...gridStyle,
            background: HEAD_BG,
            borderBottom: `1px solid ${BORDER}`,
            minHeight: compact ? 34 : 40,
          }}
        >
          {headers}
          {hasAction && <span />}
        </div>
        {rows.map((row) => (
          <div
            key={(row.team && row.team.id) || `pos-${row.position}`}
            data-testid="standings-row"
            className={rowClickable(row) ? "tp-comp-row--click" : undefined}
            onClick={rowClick(row)}
            style={{
              ...gridStyle,
              borderBottom: `1px solid ${ROW_DIVIDER}`,
              minHeight: rowMinHeight,
            }}
          >
            <ZoneKey promotion={row.promotion} height={rowMinHeight} />
            <span style={{ fontSize: 13.5, color: "#64748B", textAlign: "right", ...numStyle }}>
              {row.position}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <TeamCrest team={row.team} size={compact ? 22 : 26} />
              <span
                style={{
                  fontSize: compact ? 14 : 14.5,
                  fontWeight: 500,
                  color: "#0F172A",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {row.team && row.team.name}
              </span>
            </span>
            <span style={{ fontSize: compact ? 13 : 13.5, color: "#334155", textAlign: "right", ...numStyle }}>
              {row.played}
            </span>
            {!compact && (
              <span style={{ fontSize: 13.5, color: "#334155", textAlign: "right", ...numStyle }}>
                {row.won}
              </span>
            )}
            {!compact && (
              <span style={{ fontSize: 13.5, color: "#334155", textAlign: "right", ...numStyle }}>
                {row.drawn}
              </span>
            )}
            {!compact && (
              <span style={{ fontSize: 13.5, color: "#334155", textAlign: "right", ...numStyle }}>
                {row.lost}
              </span>
            )}
            {compact ? (
              <span style={{ fontSize: 13, color: "#475569", textAlign: "right", ...numStyle }}>
                {formatDiff(row.goal_diff)}
              </span>
            ) : (
              <span style={{ fontSize: 13.5, color: "#334155", textAlign: "right", ...numStyle }}>
                {formatGoals(row.goals_for, row.goals_against)}
              </span>
            )}
            {compact ? (
              <span style={{ fontSize: 13, color: "#334155", textAlign: "right", ...numStyle }}>
                {formatGoals(row.goals_for, row.goals_against)}
              </span>
            ) : (
              <span style={{ fontSize: 13.5, color: "#475569", textAlign: "right", ...numStyle }}>
                {formatDiff(row.goal_diff)}
              </span>
            )}
            <span
              style={{
                fontSize: 14.5,
                fontWeight: 700,
                color: "#0F172A",
                textAlign: "right",
                ...numStyle,
              }}
            >
              {row.points}
            </span>
            {!compact && (
              <span style={{ display: "flex", justifyContent: "flex-end" }}>
                <FormChips form={row.form} />
              </span>
            )}
            {hasAction && (
              <span data-testid="row-action-slot" style={{ paddingLeft: 6 }}>
                {renderRowAction(row)}
              </span>
            )}
          </div>
        ))}
      </div>
      {showLegend && <ZoneLegend promotions={legendItems} />}
    </div>
  );
};

export default CompetitionStandingsTable;
